import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Asset } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { RichText } from "~/features/ui/rich-text";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const fragment = graphql(`
  fragment PartnerCard on partner_default_Entry {
    id
    title
    url: baseUrl
    regions {
      ... on regionCategory_Category {
        id
        title
        slug
      }
    }
    services {
      ... on serviceCategory_Category {
        id
        title
        slug
      }
    }
    countries {
      ... on countryCategory_Category {
        id
        title
        slug
      }
    }
    logo: baseImage {
      ...Asset
    }
    intro: baseRichText
  }
`);
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
  theme: Theme;
};
export function PartnerCard(props: Props) {
  const {
    theme,
    mode,
    ...rest
  } = props;
  const {
    title,
    url,
    regions,
    services,
    countries,
    logo: _logo,
    intro
  } = useFragment(fragment, rest);
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const logo = takeFirst(_logo);
  return <_StyledDiv $_css={[{
    "position": "relative",
    "zIndex": "10",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "rowGap": "2.4rem",
    "padding": "2.4rem",
    "@media (min-width: 1024px)": {
      "padding": "4rem"
    }
  }, themeStyle.background]}>
      {logo && <Asset asset={logo} imageProps={{
      sizes: "lg:33vw",
      ratio: "16/9"
    }} />}
      <div>
        {title && <_StyledH>{title}</_StyledH>}
        {regions.length > 0 && <_StyledDiv2 $_css2={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP>Regions Served</_StyledP>
            <_StyledP2>
              {regions.flatMap(region => region.title).join(", ")}
            </_StyledP2>
          </_StyledDiv2>}
        {countries.length > 0 && <_StyledDiv3 $_css3={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP3>Countries</_StyledP3>
            <_StyledP4>
              {countries.flatMap(region => region.title).join(", ")}
            </_StyledP4>
          </_StyledDiv3>}
        {services.length > 0 && <_StyledDiv4 $_css4={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP5>Services</_StyledP5>
            <_StyledP6>
              {services.flatMap(region => region.title).join(", ")}
            </_StyledP6>
          </_StyledDiv4>}
      </div>
      {intro && <_StyledRichText text={intro} />}
      {url && <_StyledDiv5>
          <Button as={Link} variant="underline" icon={ArrowTopRight} href={url}>
            Visit website
          </Button>
        </_StyledDiv5>}
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-vusmvq-0"
})`${p => p.$_css}`;
var _StyledH = _styled2("h2").withConfig({
  componentId: "sc-sf-vusmvq-1"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-vusmvq-2"
})`${p => p.$_css2}`;
var _StyledP = _styled4("p").withConfig({
  componentId: "sc-sf-vusmvq-3"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP2 = _styled5("p").withConfig({
  componentId: "sc-sf-vusmvq-4"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "textTransform": "uppercase",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-vusmvq-5"
})`${p => p.$_css3}`;
var _StyledP3 = _styled7("p").withConfig({
  componentId: "sc-sf-vusmvq-6"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP4 = _styled8("p").withConfig({
  componentId: "sc-sf-vusmvq-7"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled9("div").withConfig({
  componentId: "sc-sf-vusmvq-8"
})`${p => p.$_css4}`;
var _StyledP5 = _styled10("p").withConfig({
  componentId: "sc-sf-vusmvq-9"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP6 = _styled11("p").withConfig({
  componentId: "sc-sf-vusmvq-10"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledRichText = _styled12(RichText).withConfig({
  componentId: "sc-sf-vusmvq-11"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv5 = /*#__PURE__*/_styled13("div").withConfig({
  displayName: "partner-card___StyledDiv5",
  componentId: "sc-1vyopa2-0"
})({
  "marginTop": "auto"
});